import { useCallback } from 'react';

import { graphql, useMutation } from 'react-relay';

import type { AnalyticsEventHandler } from '../../index';
import type { useUpdateNestedItemOrderMutation } from '../../ui/nested-item-drop-target/__generated__/useUpdateNestedItemOrderMutation.graphql';
import { reorderNestedItems } from '../../ui/nested-item-drop-target/async';
import { optimisticNestedItemUpdater } from '../../ui/nested-item-drop-target/utils';
import type { MenuItemReorderOperation, OrderPreference } from '../dnd-data';

type UseUpdateNestedItemOrder<T> = (args: {
	currentItems?: T[];
	currentOrder?: OrderPreference;
	entityAri: string;
	fireAnalyticsEvent?: AnalyticsEventHandler;
	getItemId: (item: T) => string;
	handleError?: (error: Error) => void;
	operation: MenuItemReorderOperation;
	ownerAri: string;
	relativeItemId?: string;
	targetItemId: string;
}) => void;

// Hook that exposes both the calculation function and the update function
export const useUpdateNestedItemOrder = () => {
	const [commitMutation] = useMutation<useUpdateNestedItemOrderMutation>(graphql`
		mutation useUpdateNestedItemOrderMutation($input: SettingsNavigationCustomisationInput!) {
			settings_updateNavigationCustomisation(input: $input) {
				sidebar {
					nodes {
						menuId
						visible
					}
				}
			}
		}
	`);

	// Helper function to calculate new order from provided current data
	const calculateNewOrderFromCurrentData = useCallback(
		(
			currentItems: unknown[],
			getItemId: (item: unknown) => string,
			currentOrder: OrderPreference,
			operation: MenuItemReorderOperation,
			targetItemId: string, // The item being moved/reordered
			relativeItemId?: string, // The position reference item (move relative to this)
		): OrderPreference => {
			const allItemIds = currentItems.map((item) => getItemId(item));

			let latestOrder = [];
			// If we have existing order, use it to determine current order
			if (currentOrder && currentOrder.length > 0) {
				// Use order for items that exist in both preference and current items
				const orderedIds = currentOrder
					.filter((item) => allItemIds.includes(getItemId(item)))
					.map((item) => getItemId(item));

				// Add any items not in preference order (newly added)
				const remainingIds = allItemIds.filter((id) => !orderedIds.includes(id));

				latestOrder = [...orderedIds, ...remainingIds];
			} else {
				// No previous order exists, use current items order
				latestOrder = allItemIds;
			}

			// Calculate new order using the reorder utility
			// targetItemId is the item being moved/reordered, relativeItemId is the position reference
			const newOrder = reorderNestedItems(operation, latestOrder, targetItemId, relativeItemId); // Convert back to sidebar format
			const result = newOrder.map((menuId: string) => ({
				menuId,
				visible: true, // All boards are visible
			}));
			return result;
		},
		[],
	);

	const updateNavigationItemOrder: UseUpdateNestedItemOrder<unknown> = useCallback(
		({
			operation,
			entityAri,
			ownerAri,
			targetItemId,
			relativeItemId,
			currentItems,
			currentOrder,
			fireAnalyticsEvent,
			getItemId,
			handleError,
		}) => {
			// If current data is provided, calculate the new order without fetching
			let calculatedSidebarOrder: OrderPreference = [];

			// First try to get fresh data from the store reader function
			let effectiveCurrentItems = currentItems;
			let effectiveCurrentSidebarOrder = currentOrder;

			if (effectiveCurrentItems && effectiveCurrentSidebarOrder) {
				// Calculate new order using current data (either from store or props)
				calculatedSidebarOrder = calculateNewOrderFromCurrentData(
					effectiveCurrentItems,
					getItemId,
					effectiveCurrentSidebarOrder,
					operation,
					targetItemId, // The item being moved
					relativeItemId, // The position reference (if any)
				);
			}

			commitMutation({
				variables: {
					input: {
						entityAri,
						ownerAri,
						sidebar: calculatedSidebarOrder.length > 0 ? calculatedSidebarOrder : [],
					},
				},
				optimisticResponse: {
					settings_updateNavigationCustomisation: {
						id: `${entityAri}-sidebar-order`,
						sidebar: {
							nodes: calculatedSidebarOrder.length > 0 ? calculatedSidebarOrder : [],
						},
					},
				},
				optimisticUpdater: (storeProxy) => {
					// Apply the optimistic update to the store for immediate UI feedback
					optimisticNestedItemUpdater({
						store: storeProxy,
						operation,
						entityAri,
						ownerAri,
						targetItemId,
						relativeItemId,
						currentItems: effectiveCurrentItems,
						getItemId,
					});
				},
				onCompleted: (_response, errors) => {
					if (errors && errors.length > 0) {
						handleError && handleError(new Error(errors[0].message));
					} else {
						fireAnalyticsEvent &&
							fireAnalyticsEvent({
								action: 'updated',
								itemId: targetItemId,
								operation,
							});
					}
				},
				onError: (error) => {
					handleError && handleError(error);
				},
			});
		},
		[commitMutation, calculateNewOrderFromCurrentData],
	);

	return updateNavigationItemOrder;
};
